import create from "zustand";
import { getCubaREST } from "@cuba-platform/react";
import { findFields } from "../../lib/fields-control";
import { AbstractBprocRequest } from "cuba/entities/base/AbstractBprocRequest";
import { getStatuses } from "../../lib/request-status-control";
import { DicRequestStatus } from "cuba/entities/base/tsadv$DicRequestStatus";
import { SerializedEntity } from "@cuba-platform/rest/dist-node/model";
import { createBpmStore } from "../bpm";
import { getBaseSteps } from "../../lib/save-request-parser";
import { mainStore } from "../../../../store";
import pick from "lodash/pick";
import { filterUndefinedFieldsDeep } from "app/entities/form-controller/lib/filter-default-value";
import {
  BASE_COMMENT_REQUIRED,
  BASE_VALIDATION_REQUIRED,
} from "app/entities/form-controller/model/store/constant";
import {
  ApplicationState,
  CreatApplicationStoreArgs,
  ValidateFn,
} from "app/entities/form-controller/model/store/types";

const createUnifiedValidate = (
  validations: Record<string, ValidateFn>,
  set: (partial: { isValidate: boolean }) => void,
): ValidateFn => {
  return async () => {
    set({ isValidate: true });

    try {
      for (const validateFn of Object.values(validations)) {
        const ok = await validateFn();
        if (!ok) return false;
      }
      return true;
    } finally {
      set({ isValidate: false });
    }
  };
};

// 🔹 фабрика стора с автоинициализацией
export const createApplicationStore = <T extends AbstractBprocRequest>({
  application,
  bpm,
}: CreatApplicationStoreArgs<T>) =>
  create<ApplicationState<T>>((set, get) => {
    const {
      baseFields,
      initialLoad,
      pathRedirect,
      defaultValues,
      cashTimeInstance = 0,
    } = application;

    const storeName = initialLoad?.id ?? "new";
    const cleanedDefaultValues = defaultValues
      ? filterUndefinedFieldsDeep(defaultValues)
      : null;

    const bpmStore = createBpmStore({
      ...bpm,
      processInstanceBusinessKey: initialLoad.id,
    });

    const store: ApplicationState<T> = {
      storeName,
      applicationKey: null,
      processKey: null,
      fields: baseFields,
      pathRedirect: pathRedirect || "",
      isSaving: false,
      isValidate: false,
      cashTimeInstance: cashTimeInstance,
      dataInstance: null,
      defaultValues: cleanedDefaultValues || null,

      formInitialized: false,
      setFormInitialized: (value) => set({ formInitialized: value }),

      employeePersonGroupId: application.employeePersonGroupId || null,

      formValues: {},

      loading: false,
      error: null,
      validationRequiredOutcomes: BASE_VALIDATION_REQUIRED,
      commentRequiredOutcomes: BASE_COMMENT_REQUIRED,

      dicStatuses: [],

      initialValue: initialLoad,

      validations: {},

      updateAndCommit: undefined,

      bpmStore,

      setApplicationKey: (key) => set({ applicationKey: key }),
      setProcessKey: (key) => set({ processKey: key }),
      getStatusRequest: () => getStatuses(get().dataInstance),

      setDicStatuses: (dicStatus: SerializedEntity<DicRequestStatus>[]) => {
        set({ dicStatuses: dicStatus });
      },

      loadApplication: async ({ entityName, id, view, form }) => {
        set({ loading: true, error: null });

        try {
          const res = await getCubaREST().loadEntity<T>(entityName, id, {
            view,
          });

          const fieldValues = findFields(get().fields, res, entityName);

          set({
            dataInstance: res,
            formValues: fieldValues,
            loading: false,
          });

          if (get().setData) {
            get().setData(res, fieldValues);
          }
          if (get().formValues && Object.keys(get().formValues).length) {
            const safeFormValues = pick(get().formValues, get().fields);
            setTimeout(() => form.setFieldsValue(safeFormValues), 0);
          }
          return res;
        } catch (e) {
          set({ error: e.message ?? String(e), loading: false });
          return null;
        }
      },

      setEmployeePersonGroupId: (id) => set({ employeePersonGroupId: id }),

      applyLoadedDataToForm: (data, form) => {
        const { setData, fields } = get();

        if (data) {
          if (setData) {
            setData(data.dataInstance, data.formValues);
          }
          if (data.formValues && Object.keys(data.formValues).length) {
            const safeFormValues = pick(data.formValues, fields);
            requestAnimationFrame(() => {
              form.setFieldsValue(safeFormValues);
            });
          }
        }
      },

      setData: (dataInstance, formValues) => set({ dataInstance, formValues }),
      resetData: () => {
        set({
          dataInstance: null,
          formValues: {},
          error: null,
          loading: false,
        });
      },
      setIsSaving: (isSaving) =>
        set({
          isSaving,
        }),
      validate: async () => true,

      setValidate: (fn, key) => {
        set((state) => {
          const resolvedKey = key ?? `auto_${Date.now()}_${Math.random()}`;

          const newValidations = {
            ...state.validations,
            [resolvedKey]: fn,
          };

          return {
            ...state,
            validations: newValidations,
            validate: createUnifiedValidate(newValidations, set),
          };
        });
      },

      clearValidate: (key) => {
        set((state) => {
          // Очистить всё
          if (!key) {
            return {
              ...state,
              validations: {},
              validate: async () => true,
            };
          }

          // Удалить конкретный валидатор
          const newValidations = { ...state.validations };
          delete newValidations[key];

          return {
            ...state,
            validations: newValidations,
            validate: createUnifiedValidate(newValidations, set),
          };
        });
      },

      getUpdatedInstance: (form) => {
        const steps = getBaseSteps({
          form: form,
          fields: get().fields,
          mainStore: mainStore,
          initialValue: get().initialValue,
          dataInstance: get().dataInstance,
        });

        const rawData = steps.getRawData();
        return steps.prepareData(rawData);
      },

      setOnUpdateSuccess: (cb) => {
        set({ onUpdateSuccess: cb });
      },

      setUpdateAndCommit: (fn, form) =>
        set(() => ({
          updateAndCommit: async () => {
            set({ isSaving: true });

            try {
              const {
                fields,
                initialValue,
                dataInstance,
                formValues,
                setData,
                onUpdateSuccess,
              } = get();

              const steps = getBaseSteps({
                form,
                fields,
                mainStore,
                initialValue,
                dataInstance,
              });

              const result = await fn(steps);

              if (result?.id) {
                const updatedDataInstance = {
                  ...dataInstance,
                  id: result.id,
                };

                bpmStore.getState().setKeys({
                  processInstanceBusinessKey: result.id,
                });

                setData(updatedDataInstance, formValues);

                if (onUpdateSuccess) {
                  onUpdateSuccess(result);
                }

                return result;
              }

              return result;
            } finally {
              set({ isSaving: false, isValidate: false });
            }
          },
        })),
    };

    return store;
  });
